import useVisitName from '../../hooks/useVisitName';
import { APITypes, MedicalDataTypes } from '../../primary-record-types';
import { resourceTypeGuards } from '../medicalDataUtils';
import { toTitleCase } from '../toTitleCase';

const isDetailsResponse = (
    resource:
        | APITypes.medicalDataService.ResourceDetailsResponse<MedicalDataTypes.PrimaryRecordResource>
        | APITypes.medicalDataService.ListResourceResponseItem<MedicalDataTypes.PrimaryRecordResource>
        | null,
): resource is APITypes.medicalDataService.ResourceDetailsResponse<MedicalDataTypes.PrimaryRecordResource> =>
    !!resource && 'resource' in resource;

export const getNonVisitResourceTitle = (
    data:
        | APITypes.medicalDataService.ResourceDetailsResponse<MedicalDataTypes.PrimaryRecordResource>
        | APITypes.medicalDataService.ListResourceResponseItem<MedicalDataTypes.PrimaryRecordResource>
        | null,
): string => {
    if (!data) return '';

    const resource = isDetailsResponse(data) ? data.resource : data || null;

    if (
        resourceTypeGuards.isCondition(resource) ||
        resourceTypeGuards.isAllergy(resource) ||
        resourceTypeGuards.isImaging(resource) ||
        resourceTypeGuards.isProcedure(resource)
    ) {
        return toTitleCase(resource.name, { ignoreAllCaps: true });
    } else if (resourceTypeGuards.isMedication(resource)) {
        let title = toTitleCase(resource.name, { ignoreAllCaps: true });
        if (resource.genericNameOrActiveIngredient) title += ` (${resource.genericNameOrActiveIngredient})`;
        return title;
    } else if (resourceTypeGuards.isVaccination(resource)) {
        return toTitleCase(resource.parentCoding?.display || resource.vaccineType.display, {
            ignoreAllCaps: true,
        });
    } else if (resourceTypeGuards.isLab(resource)) {
        return toTitleCase(resource.labType.display, { ignoreAllCaps: true });
    } else {
        return '';
    }
};

export const useResourceTitle = (
    data:
        | APITypes.medicalDataService.ResourceDetailsResponse<MedicalDataTypes.PrimaryRecordResource>
        | APITypes.medicalDataService.ListResourceResponseItem<MedicalDataTypes.PrimaryRecordResource>
        | null,
    medicalProfileId: string,
): string => {
    const resource = isDetailsResponse(data) ? data.resource : data || null;

    const visitName = useVisitName({ resource: resource || null, medicalProfileId });

    if (!resource) return '';

    if (resourceTypeGuards.isVisit(resource)) {
        return visitName || '';
    } else {
        return getNonVisitResourceTitle(data);
    }
};
